import { useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { env } from '@/config/env';

export type MetricScope = 'safety' | 'revenue' | 'launch_gates';

interface MetricsState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Polls the `admin-metrics` edge function on a fixed interval. The
 * function returns a different JSON shape per scope, so consumers must
 * narrow `T` themselves (or rely on the dashboard pages that already
 * know their own shape).
 *
 * Poll cadence: 60s. Anything faster wastes function invocations; the
 * dashboards do not need sub-minute freshness for operational metrics.
 */
const POLL_MS = 60_000;

export const useAdminMetrics = <T = unknown>(scope: MetricScope): MetricsState<T> => {
  const [state, setState] = useState<MetricsState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    let cancelled = false;
    const url = `${env.supabaseUrl}/functions/v1/admin-metrics?scope=${scope}`;

    const fetchOnce = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) {
          if (cancelled) return;
          setState((prev) => ({ ...prev, loading: false, error: `HTTP ${res.status}` }));
          return;
        }
        const json = (await res.json()) as T;
        if (cancelled) return;
        setState({ data: json, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'fetch failed',
        }));
      }
    };

    fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [scope]);

  return state;
};
